/**
 * Who is signed in, as the API says (`GET /auth/me`), cached by TanStack
 * Query. The server decides what the person must do next (`nextStep`):
 * enter a code, change a one-time password, set up codes, or choose an
 * authority; `pathForStep` turns that into a page.
 */
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { api, setCsrfToken, type SignedInUser, unwrap } from "@/api/client";

export const SESSION_KEY = ["session"] as const;

export async function fetchSession(): Promise<SignedInUser | null> {
  const result = await api.GET("/api/v1/auth/me");
  if (result.response.status === 401) {
    setCsrfToken(null);
    return null;
  }
  const session = unwrap(result);
  setCsrfToken(session.csrfToken);
  return session;
}

export function useSession() {
  return useQuery({ queryKey: SESSION_KEY, queryFn: fetchSession, staleTime: 60_000 });
}

/** Stores a session the API just returned (sign-in, code, authority). */
export function useSetSession(): (session: SignedInUser | null) => void {
  const queryClient = useQueryClient();
  return (session) => {
    setCsrfToken(session?.csrfToken ?? null);
    queryClient.setQueryData(SESSION_KEY, session);
  };
}

export type NextStep = SignedInUser["nextStep"];

export const STEP_PATHS: Record<NextStep, string> = {
  NONE: "/",
  ENTER_MFA_CODE: "/sign-in/code",
  CHANGE_PASSWORD: "/account/password",
  ENROL_MFA: "/account/codes",
  CHOOSE_AUTHORITY: "/choose-authority",
};

export function pathForStep(step: NextStep): string {
  return STEP_PATHS[step];
}

/** The person's role where they are working now, or PLATFORM. */
/** Whether this installation uses sign-in codes at all (ADR-0041). */
export function useSignInCodes(): boolean {
  return useSession().data?.signInCodes ?? false;
}

export function currentRole(session: SignedInUser): string | null {
  if (session.authority) {
    return session.authority.role;
  }
  return session.user.isPlatformAdmin ? "PLATFORM" : null;
}

/**
 * The colour card the authority in use reads drawings by: MMSGNA's (M40),
 * the AUBB card for every building (M44), or none (OBPAS's layer names).
 */
export function useColourCard(): "MMSGNA" | "AUBB" | null {
  const standard = useSession().data?.authority?.drawingStandard;
  if (standard === "MMSGNA-2026.10") return "MMSGNA";
  if (standard === "AUBB-2026.10") return "AUBB";
  return null;
}

/** Whether the authority in use checks against its byelaw knowledge base (M34). */
export function useKnowledgeBaseOn(): boolean {
  return useSession().data?.authority?.byelawKnowledgeBase ?? false;
}
